import {
  Modal,
  Stack,
  Box,
  Typography,
  IconButton,
  Autocomplete,
  TextField,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { desktopSx } from "src/utils/styles/modalStyles";
import { useGetProductsQuery } from "src/api/services/productsApi";
import { FormEvent, useCallback, useState } from "react";
import { Product } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { useCreateExperimentMutation } from "src/api/services/experimentsApi";
import { useSentryLogger } from "src/hooks/useSentryLogger";

interface Props {
  open: boolean;
  onClose: () => void;
}

const CreateNewExperimentModal = ({ onClose, open }: Props) => {
  const logError = useSentryLogger("error");
  const [createExperiment, { isLoading, error: submitError }] =
    useCreateExperimentMutation();

  const [error, setError] = useState("");
  const { products } = useGetProductsQuery(undefined, {
    selectFromResult: ({ data }) => {
      return { products: data?.data ?? [] };
    },
  });
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const handleName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setName(event.target.value);
      setError("");
    },
    [],
  );

  const handleDescription = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setDescription(event.target.value);
      setError("");
    },
    [],
  );

  const handleProduct = useCallback(
    (_: React.SyntheticEvent<Element, Event>, value: Product[]) => {
      setSelectedProducts(value);
      setError("");
    },
    [],
  );

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (selectedProducts.length === 0) {
        setError("Product is required");
        return;
      }

      try {
        await createExperiment({
          productArgs: selectedProducts.map((p) => ({
            id: p.id,
            category_id: p.category.id,
          })),
          name,
          description,
        });
        onClose();
      } catch (e) {
        setError("Failed to create experiment");
        logError("Failed to create experiment", e);
      }
    },
    [createExperiment, description, logError, name, onClose, selectedProducts],
  );

  return (
    <Modal open={open}>
      <Stack
        sx={desktopSx}
        gap={2}
        component="form"
        maxWidth={400}
        onSubmit={handleSubmit}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="h5" fontWeight={700}>
            Add Experiment
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Box>
        <Stack gap={2}>
          <Autocomplete
            aria-required
            multiple
            options={products}
            getOptionLabel={(p) => p.name}
            value={selectedProducts}
            onChange={handleProduct}
            renderInput={(params) => <TextField {...params} label="Product" />}
          />
          <TextField
            label="Experiment Name"
            required
            value={name}
            onChange={handleName}
          />
          <TextField
            label="Description"
            required
            value={description}
            onChange={handleDescription}
          />

          <LoadingButton type="submit" loading={isLoading}>
            Create experiment
          </LoadingButton>
        </Stack>
        {error ? <Alert severity={"error"}>{error}</Alert> : null}
        {submitError ? (
          <Alert severity={"error"}>{JSON.stringify(submitError)}</Alert>
        ) : null}
      </Stack>
    </Modal>
  );
};

export default CreateNewExperimentModal;
